import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Li,
  Progress,
  SimpleList,
  Switch,
  Text,
  TitleBar
} from "@boulevard/blvd-ui";
import "@boulevard/blvd-ui/index.css";
import * as Comlink from "comlink";
import CurrencyInput from "./CurrencyInput";
import "./App.css";
import {
  CREATE_BUSINESS_SCHEMA_FIELD,
  CREATE_REPORT,
  CREATE_SERVICE_SCHEMA_FIELD,
  CREATE_PRODUCT,
  CREATE_PRODUCT_CATEGORY,
  BUSINESS_CONFIGURATION,
  SAVE_BUSINESS_FIELD,
  SERVICE_MENU
} from "./graph";

const api: any = Comlink.wrap(Comlink.windowEndpoint(window.parent));

// The proxy must be released on window unload rather than
// a lifecycle within React because this app runs inside of
// an iframe which can be destroyed from the outside
window.addEventListener("unload", api[Comlink.releaseProxy]);

type Category = {
  id: string;
  name: string;
  open: boolean;
  services: Service[];
};

type ProductCategory = {
  id: string;
  name: string;
};

type ProductCategoryNode = {
  node: ProductCategory;
};

type Service = {
  fee: number | null;
  id: string;
  modified: boolean;
  name: string;
  open: boolean;
};

type ServiceCategory = {
  category: Category;
  service: Service;
  idx: string;
}

function App() {
  const [clientFee, setClientFee] = useState<number>(0);
  const [serviceMenu, setServiceMenu] = useState<Category[]>([]);
  const [lastEditedFee, setLastEditedFee] = useState<number>(0);

  const saveConfiguration = async () => {
    await api.submitRequest({
      query: `
        mutation {
          updateBusiness(input: {
            customFields: [{
              key: "greenCircleClientFee",
              integerValue: ${clientFee}
            }]
          }) {
            business {
              id
            }
          }
        }
      `
    });

    const services = serviceMenu.flatMap((category: Category, cIdx: number): ServiceCategory[] => {
      return category.services.map((service: Service, sIdx: number) => {
        return {
          category: category,
          service: service,
          idx: `${cIdx}${sIdx}`
        };
      });
    })

    const chunkSize = 200;

    chunk(services, chunkSize).forEach(serviceChunk => {
      const queries = serviceChunk.map((item: ServiceCategory): string => {
        return `
          service${item.idx}: updateService(input: {
            id: "${item.service.id}",
            customFields: [{
              key: "greenCircleFee",
              integerValue: ${item.category.open && item.service.open ? item.service.fee : null}
            }]
          }) {
            service {
              id
            }
          }
        `
      });

      api.submitRequest({
        query: `
          mutation {
            ${queries.join("\n")}
          }
        `
      });
    });

    api.appConfigContext.onClose();
  };

  const chunk = (arr: Array<any>, size: number) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
      arr.slice(i * size, i * size + size)
    );

  const createProductCategory = async (): Promise<{ id: string }> => {
    const {
      data: {
        createProductCategory: {
          productCategory: { id }
        }
      }
    } = await api.submitRequest({
      query: CREATE_PRODUCT_CATEGORY,
      variables: {
        input: {
          name: "Green Circle Salon",
          commissionEnabled: false
        }
      }
    });

    return { id };
  };

  const createProduct = async (
    categoryId: string
  ): Promise<{ id: string }> => {
    const {
      data: {
        createProduct: {
          product: { id }
        }
      }
    } = await api.submitRequest({
      query: CREATE_PRODUCT,
      variables: {
        input: {
          name: "Green Circle Salon Fee",
          categoryId,
          unitPrice: 0,
          taxable: false
        }
      }
    });

    return { id };
  };

  const createBusinessSchemaFields = async (): Promise<void> => {
    await api.submitRequest({
      query: CREATE_BUSINESS_SCHEMA_FIELD,
      variables: {
        input: {
          schema: "BUSINESS",
          key: "greenCircleProductId",
          name: "Green Circle Salon Product Id",
          valueType: "TEXT"
        }
      }
    });

    await api.submitRequest({
      query: CREATE_BUSINESS_SCHEMA_FIELD,
      variables: {
        input: {
          schema: "BUSINESS",
          key: "greenCircleClientFee",
          name: "Green Circle Salon Client Fee",
          valueType: "INTEGER"
        }
      }
    });
  };

  const createServiceSchemaField = async (): Promise<void> => {
    await api.submitRequest({
      query: CREATE_SERVICE_SCHEMA_FIELD,
      variables: {
        input: {
          schema: "SERVICE",
          key: "greenCircleFee",
          name: "Green Circle Salon Fee",
          valueType: "INTEGER"
        }
      }
    });
  };

  const saveBusinessField = async (productId: string): Promise<void> => {
    await api.submitRequest({
      query: SAVE_BUSINESS_FIELD,
      variables: {
        input: {
          customFields: [
            {
              key: "greenCircleProductId",
              textValue: productId
            }
          ]
        }
      }
    });
  };

  const fetchServiceMenu = async (): Promise<any> => {
    return await api.submitRequest({
      query: SERVICE_MENU
    });
  };

  const createReport = async (): Promise<void> => {
    await api.submitRequest({
      query: CREATE_REPORT,
      variables: {
        input: {
          templateId: "GREEN_CIRCLE_SALES",
          name: "Green Circle Salon"
        }
      }
    });
  };

  useEffect(() => {
    (async () => {
      const {
        data: {
          productCategories: { edges: productCategoryNodes },
          business: {
            custom: { greenCircleProductId, greenCircleClientFee }
          }
        }
      } = await api.submitRequest({
        query: BUSINESS_CONFIGURATION
      });

      setClientFee(greenCircleClientFee || 0);

      const greenCircleProductCategoryNode = productCategoryNodes.find(
        ({ node }: ProductCategoryNode): boolean =>
          // older installations were named "Green Circle" and now it's "Green Circle Salon"
          // See commit: 52f5acff48574dc1899a238d1feffab67b08c2e3
          node.name.startsWith("Green Circle")
      );

      if (!greenCircleProductCategoryNode) {
        var { id: productCategoryId } = await createProductCategory();
      }

      if (!greenCircleProductId) {
        const { id: productId } = await createProduct(productCategoryId);
        await createBusinessSchemaFields();
        createServiceSchemaField();
        createReport();
        await saveBusinessField(productId);
      }

      const {
        data: {
          serviceCategories: { edges }
        }
      } = await fetchServiceMenu();

      setServiceMenu(
        edges.filter(({ node }: any) => node.active).map(
          ({ node }: any): Category => ({
            name: node.name,
            id: node.id,
            open: node.services.edges.filter(({ node }: any) => node.active).some(({ node }: any) =>
              Boolean(node.custom.greenCircleFee)
            ),
            services: node.services.edges.filter(({ node }: any) => node.active).map(
              ({ node }: any): Service => ({
                open: Boolean(node.custom.greenCircleFee),
                name: node.name,
                id: node.id,
                fee: node.custom.greenCircleFee,
                modified: Boolean(node.custom.greenCircleFee)
              })
            )
          })
        )
      );
    })();
  }, []);

  const updateCategory = (
    category: Category,
    updates: Partial<Category>
  ): Category[] => {
    return serviceMenu.map(
      (c: Category): Category => ({
        ...c,
        ...(category.id === c.id ? updates : {}),
        services: c.services.map((s: Service): Service => {
          const serviceUpdates = { open: s.open, fee: s.fee };
          // Open all services within provided category
          // and apply a fee update to an unmodified service
          if (category.id === c.id && updates.open) {
            serviceUpdates.open = true;

            if (!s.modified) {
              serviceUpdates.fee = lastEditedFee;
            }
          }

          return {
            ...s,
            ...serviceUpdates
          };
        })
      })
    );
  };

  const updateService = (
    service: Service,
    updates: Partial<Service>
  ): Category[] => {
    return serviceMenu.map(
      (c: Category): Category => ({
        ...c,
        services: c.services.map(
          (s: Service): Service => ({
            ...s,
            // Apply updates to the provided service
            ...(service.id === s.id ? updates : {})
          })
        )
      })
    );
  };

  const renderClientFee = (): JSX.Element => {
    return (
      <div className="flex-center client-fee">
        <div className="flex-center">
          <Text as="h5" variant="h5" className="heading heading-inline">
            Per Client Fee
          </Text>
        </div>
        <CurrencyInput
          value={clientFee}
          onBlur={(newVal: number): void => setClientFee(newVal)}
        />
      </div>
    );
  };

  const renderService = (service: Service): JSX.Element => {
    return (
      <Li key={service.id}>
        <div className="flex-center service-li">
          <div className="flex-center">
            <Switch
              size="small"
              checked={service.open}
              className="switch-field"
              id={`switch-${service.id}`}
              name={service.name}
              onChange={() => {
                setServiceMenu(
                  updateService(service, {
                    open: !service.open,
                    fee: !service.open ? lastEditedFee : service.fee
                  })
                );
              }}
            />
            <Text variant="body3" as="label" htmlFor={`switch-${service.id}`}>
              {service.name}
            </Text>
          </div>
          {service.open && (
            <CurrencyInput
              value={service.fee || 0}
              onFocus={(e: React.FormEvent<HTMLInputElement>): void => {
                const target = e.target as HTMLInputElement;

                if (!service.modified) {
                  setServiceMenu(
                    updateService(service, { fee: lastEditedFee })
                  );
                }

                setTimeout(() => target.select(), 10);
              }}
              onBlur={(newVal: number): void => {
                setLastEditedFee(newVal);
                setServiceMenu(
                  updateService(service, { fee: newVal, modified: true })
                );
              }}
            />
          )}
        </div>
      </Li>
    );
  };

  const renderCategory = (category: Category): JSX.Element => {
    return (
      <Card key={category.id} padding={0} className="service-category">
        <TitleBar>
          <div className="flex-center">
            <Switch
              size="small"
              checked={category.open}
              className="switch-field"
              id={`switch-${category.id}`}
              name={category.name}
              onChange={() => {
                setServiceMenu(
                  updateCategory(category, { open: !category.open })
                );
              }}
            />
            <Text
              as="label"
              htmlFor={`switch-${category.id}`}
              style={{ fontWeight: 500, lineHeight: "16.25px" }}
              variant="body3"
            >
              {category.name}
            </Text>
          </div>
        </TitleBar>
        <SimpleList size="small">
          {category.open &&
            category.services.map((service: Service) =>
              renderService(service)
            )}
        </SimpleList>
      </Card>
    );
  };

  const isLoaded = serviceMenu.length;

  if (!isLoaded) {
    return (
      <div data-testid="green-circle" className="green-circle-wrapper">
        <Progress variant="indeterminate" />
      </div>
    );
  }

  return (
    <div data-testid="green-circle" className="green-circle-wrapper">
      <div className="green-circle-body">
        <Text as="p" variant="body3">
          Assign your preferred Green Circle Salon Environmental Fees. You
          will be able to adjust or remove these fees during checkout.
        </Text>

        <Text as="p" variant="body3">
          Fees will show under Product Sales reports as "Green Circle Salon
          Fee"
        </Text>

        {renderClientFee()}

        <br />

        <Text as="h5" variant="h5" className="heading heading-underline">
          Per Service Fees
        </Text>

        {serviceMenu.map((category: Category) => renderCategory(category))}
      </div>
      <div className="green-circle-footer">
        <Button onClick={saveConfiguration}>Save</Button>
      </div>
    </div>
  );
}

export default App;
